import React from 'react';
import {
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormControlProps,
  RadioGroup,
  RadioGroupProps,
  FormHelperText,
  FormHelperTextProps,
  FormLabel,
  FormLabelProps,
} from '@mui/material';
import { Field, useField } from 'react-final-form';

import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';

import { hasError } from './utils';

import { Radio, RadioProps } from '../components';

export interface RadioItem {
  value: unknown;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface RadiosProps {
  name: string;
  data: RadioItem[];
  label?: React.ReactNode;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  // it's a way to pass props to internal components
  fieldProps?: Partial<Parameters<typeof Field>[0]>;
  formControlLabelProps?: Partial<FormControlLabelProps>;
  formControlProps?: Partial<FormControlProps>;
  radioGroupProps?: Partial<RadioGroupProps>;
  formHelperTextProps?: Partial<FormHelperTextProps>;
  formLabelProps?: Partial<FormLabelProps>;
  radioProps?: Partial<RadioProps>;
}

interface RadioWrapperProps {
  name: string;
  data: RadioItem;
  rootDisabled?: boolean;
  fieldProps: RadiosProps['fieldProps'];
  formControlLabelProps: RadiosProps['formControlLabelProps'];
  radioProps: RadiosProps['radioProps'];
}

const RadioWrapper: React.FC<RadioWrapperProps> = ({
  name,
  data,
  rootDisabled,
  fieldProps,
  formControlLabelProps,
  radioProps,
}) => {
  const { isEditable } = useCanEditFormFieldsContext();
  const disabled = rootDisabled || data.disabled || !isEditable;

  return (
    <FormControlLabel
      label={data.label}
      name={name}
      disabled={disabled}
      value={data.value}
      control={
        <Field
          type='radio'
          name={name}
          render={(fieldProps) => {
            const {
              input: { name, value, onChange, checked },
            } = fieldProps;
            return (
              <Radio
                name={name}
                value={value}
                onChange={onChange}
                checked={checked}
                disabled={disabled}
                {...radioProps}
              />
            );
          }}
          {...fieldProps}
        />
      }
      {...formControlLabelProps}
    />
  );
};

export const Radios: React.FC<RadiosProps> = (props) => {
  const {
    label,
    data,
    name,
    required,
    disabled,
    fieldProps,
    formControlLabelProps,
    formControlProps,
    radioGroupProps,
    formHelperTextProps,
    formLabelProps,
    radioProps,
  } = props;
  const { meta } = useField(name);
  const isError = hasError(meta);

  const { error, submitError } = meta;
  const errorText = error || submitError;
  const helperText = isError ? errorText : props.helperText;

  return (
    <FormControl error={isError} required={required} {...formControlProps}>
      {label && <FormLabel {...formLabelProps}>{label}</FormLabel>}
      <RadioGroup {...radioGroupProps}>
        {data.map((item, index) => {
          return (
            <RadioWrapper
              key={index}
              name={name}
              data={item}
              rootDisabled={disabled}
              fieldProps={fieldProps}
              formControlLabelProps={formControlLabelProps}
              radioProps={radioProps}
            />
          );
        })}
      </RadioGroup>
      <FormHelperText {...formHelperTextProps}>{helperText}</FormHelperText>
    </FormControl>
  );
};
